import { Injectable, inject } from '@angular/core';
import { catchError, lastValueFrom, tap, throwError } from 'rxjs';
import {
  ChatService,
  ChatOneByOneDto,
  ChatDto,
  ChatManagementService,
  AddUserToChatDto,
  RemoveUserToChatDto,
  EditChatDto,
  ChatBaseDto,
  UsersService,
} from '@src/api-messenger';
import { AlertService } from '@src/core/services';

import { AddMemberModel } from '../types';
import { mapChatMemberToMember, mapMessageViewToMessage } from '../utils';
import { COUNT_UPLOADED_MESSAGES } from '../constants';

import { StoreService } from './store.service';

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  private readonly store = inject(StoreService);
  private readonly chatService = inject(ChatService);
  private readonly chatManagementService = inject(ChatManagementService);
  private readonly usersService = inject(UsersService);
  private readonly alertService = inject(AlertService);

  private nextTimestamp?: number | null;

  getChatFullInfo(chatId: string) {
    return this.chatService.apiChatChatIdGet(chatId).pipe(
      catchError(err => {
        this.alertService.error(err);
        return throwError(() => err);
      }),
      tap(chatFullInfo => {
        this.store.updateChatsState([chatFullInfo]);
      }),
    );
  }

  getChatsForCurrentUser(userId: string) {
    return this.getAttachedChats(userId, false);
  }

  getChatsByAttachId(attachId: string) {
    return this.getAttachedChats(attachId, true);
  }

  private getAttachedChats(attachId: string, useAttachId = true) {
    return this.chatService
      .apiChatListGet(useAttachId ? attachId : undefined)
      .pipe(
        catchError(err => {
          this.alertService.error(err);
          return throwError(() => err);
        }),
      )
      .pipe(
        tap(chats => {
          // TODO: обсудить с бэком перенос этих данный в метод получения списка чатов, либо в отдельный метод
          // INFO: для получения последних сообщений
          chats.forEach(chat => chat.isChatMember && this.getChatFullInfo(chat.chatId).subscribe());

          this.store.setChatsByAttachId(attachId, chats);
        }),
      );
  }

  createPublicChat(data: ChatBaseDto) {
    return this.chatService.apiChatCreatePublicPost(data).pipe(
      catchError(err => {
        this.alertService.error(err);
        return throwError(() => err);
      }),
    );
  }

  createPrivateChat(data: ChatOneByOneDto) {
    return this.chatService.apiChatCreateDirectPost(data).pipe(
      catchError(err => {
        this.alertService.error(err);
        return throwError(() => err);
      }),
    );
  }

  createGroupChat(data: ChatDto) {
    return this.chatService.apiChatCreateNewPost(data).pipe(
      catchError(err => {
        this.alertService.error(err);
        return throwError(() => err);
      }),
    );
  }

  deleteChat(chatId: string) {
    return this.chatService.apiChatDeletePost({ chatId }).pipe(
      catchError(err => {
        this.alertService.error(err);
        return throwError(() => err);
      }),
    );
  }

  leaveChat(chatId: string) {
    return this.chatService.apiChatLeavePost({ chatId }).pipe(
      catchError(err => {
        this.alertService.error(err);
        return throwError(() => err);
      }),
    );
  }

  createChat(data: ChatDto) {
    return this.chatService.apiChatCreateNewPost(data).pipe(
      catchError(err => {
        this.alertService.error(err);
        return throwError(() => err);
      }),
    );
  }

  editChat(chatId: string, data: EditChatDto) {
    return this.chatService.apiChatEditChatIdPost(chatId, data).pipe(
      catchError(err => {
        this.alertService.error(err);
        return throwError(() => err);
      }),
    );
  }

  setChatNotificationsSettings(chatId: string, mute: boolean) {
    if (mute) {
      return this.chatService.apiChatSettingsMuteChatIdPost(chatId).pipe(
        catchError(err => {
          this.alertService.error(err);
          return throwError(() => err);
        }),
      );
    }

    return this.chatService.apiChatSettingsUnmuteChatIdPost(chatId).pipe(
      catchError(err => {
        this.alertService.error(err);
        return throwError(() => err);
      }),
    );
  }

  openChat(chatId: string, connectionId: string) {
    return this.chatService.apiChatOpenPost({ chatId, connectionId }).pipe(
      catchError(err => {
        this.alertService.error(err);
        return throwError(() => err);
      }),
    );
  }

  closeChat(chatId: string, connectionId: string) {
    return this.chatService.apiChatClosePost({ chatId, connectionId }).pipe(
      catchError(err => {
        this.alertService.error(err);
        return throwError(() => err);
      }),
    );
  }

  getChatMessages(chatId: string, nextPage = false) {
    if (nextPage && this.nextTimestamp === null) {
      return;
    }

    return this.chatService
      .apiChatMessagesChatIdGet(
        chatId,
        nextPage ? (this.nextTimestamp ?? undefined) : undefined,
        COUNT_UPLOADED_MESSAGES,
      )
      .pipe(
        catchError(err => {
          this.alertService.error(err);
          return throwError(() => err);
        }),
        tap(messagesApi => {
          this.nextTimestamp = messagesApi.nextTimestamp;
          const messages = messagesApi.messages?.map(message => mapMessageViewToMessage(message, chatId)) ?? [];
          this.store.setChatMessagesState(chatId, messages);
        }),
      );
  }

  getChatPermissions(chatId: string) {
    return this.chatService.apiChatPermissionsChatIdGet(chatId).pipe(
      catchError(err => {
        this.alertService.error('Вы не являетесь участником чата, для получения доступа обратитесь к администратору');
        return throwError(() => err);
      }),
      tap(permissions => {
        this.store.setChatPermissionsState(chatId, permissions);
      }),
    );
  }

  getChatMembers(chatId: string) {
    return this.chatService
      .apiChatMembersChatIdGet(chatId)
      .pipe(
        catchError(err => {
          this.alertService.error(err);
          return throwError(() => err);
        }),
      )
      .pipe(
        tap(members => {
          const chatMembers = members.map(member => mapChatMemberToMember(member));
          this.store.setChatMembersState(chatId, chatMembers);
        }),
      );
  }

  addChatMembers(chatId: string, members: Array<AddMemberModel>) {
    return Promise.all(
      members.map(user => {
        return lastValueFrom(
          this.chatManagementService
            .apiChatManageUserAddPost({
              chatId,
              userId: user.userId,
            })
            .pipe(
              catchError(err => {
                this.alertService.error(err);
                return throwError(() => err);
              }),
            ),
        );
      }),
    );
  }

  removeChatMember(chatId: string, userId: string) {
    return this.chatManagementService
      .apiChatManageUserRemovePost({
        chatId,
        userId,
      })
      .pipe(
        catchError(err => {
          this.alertService.error(err);
          return throwError(() => err);
        }),
      );
  }

  addAdmin(data: AddUserToChatDto) {
    return this.chatManagementService.apiChatManageAdminAddPost(data).pipe(
      catchError(err => {
        this.alertService.error(err);
        return throwError(() => err);
      }),
    );
  }

  removeAdmin(data: RemoveUserToChatDto) {
    return this.chatManagementService.apiChatManageAdminRemovePost(data).pipe(
      catchError(err => {
        this.alertService.error(err);
        return throwError(() => err);
      }),
    );
  }

  userNameChanged(userId: string) {
    return this.usersService.apiUserNameChangedPost({ userId }).pipe(
      catchError(err => {
        this.alertService.error(err);
        return throwError(() => err);
      }),
    );
  }

  userAvatarChanged(userId: string) {
    return this.usersService.apiUserAvatarChangedPost({ userId }).pipe(
      catchError(err => {
        this.alertService.error(err);
        return throwError(() => err);
      }),
    );
  }
}
